import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, CircleNotch, Eye, EyeSlash, CheckCircle, Sparkle } from "@phosphor-icons/react";

function AuthShell({ children, eyebrow, title, subtitle }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--bg)" }}>
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden" style={{ background: "var(--bg-soft)" }}>
        <div className="blob-warm" style={{ width: 480, height: 480, top: -120, right: -140 }} />
        <div className="blob-warm" style={{ width: 320, height: 320, bottom: -60, left: -100, opacity: 0.4 }} />
        <Link to="/" className="flex items-center gap-2 relative z-10">
          <div className="w-8 h-8 rounded-full" style={{ background: "var(--grad-warm)" }} />
          <span className="font-semibold">BrandLoop</span>
        </Link>
        <div className="relative z-10">
          <div className="eyebrow mb-3 flex items-center gap-2" style={{ color: "var(--accent-2)" }}>
            <Sparkle size={12} weight="fill" /> Your first hire
          </div>
          <p className="font-serif text-3xl leading-tight" style={{ maxWidth: 420 }}>
            She keeps your work safe. And your account too.
          </p>
        </div>
        <div />
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md fade-up">
          <div className="eyebrow mb-2">{eyebrow}</div>
          <h1 className="font-serif text-4xl font-semibold mb-2 tracking-tight" style={{ textWrap: "balance" }}>{title}</h1>
          {subtitle && <p className="text-sm mb-8" style={{ color: "var(--ink-2)" }}>{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  if (sent) {
    return (
      <AuthShell eyebrow="Reset password" title="Check your inbox.">
        <div className="p-6 rounded-3xl mb-5 flex items-start gap-3" data-testid="forgot-sent" style={{ background: "var(--accent-tint)", border: "1px solid var(--accent)" }}>
          <CheckCircle size={22} weight="fill" style={{ color: "var(--accent)" }} />
          <div>
            <div className="font-medium mb-1">If <span style={{ color: "var(--ink)" }}>{email}</span> is registered, a reset link is on the way.</div>
            <div className="text-sm" style={{ color: "var(--ink-2)" }}>The link stays valid for one hour. Check your spam folder too, sometimes she ends up there.</div>
          </div>
        </div>
        <Link to="/login" className="btn btn-ghost w-full" data-testid="forgot-back-login">Back to sign in</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Reset password"
      title="Forgot your password?"
      subtitle="Type the email you signed up with. I'll send you a one-time link to set a new one."
    >
      <form onSubmit={submit}>
        <label className="block mb-6">
          <div className="eyebrow mb-2">Email</div>
          <input required type="email" data-testid="forgot-email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoFocus />
        </label>
        <button disabled={busy || !email} type="submit" data-testid="forgot-submit" className="btn btn-accent w-full btn-lg mb-4">
          {busy ? <><CircleNotch size={14} className="animate-spin" /> Sending</> : <>Send reset link <ArrowRight size={16} /></>}
        </button>
        <div className="text-sm" style={{ color: "var(--ink-3)" }}>
          Remembered it? <Link to="/login" className="link-underline" style={{ color: "var(--ink)" }} data-testid="forgot-back">Back to sign in →</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token. Request a new reset link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (pw !== pw2) {
      toast.error("Passwords don't match.");
      return;
    }
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2000);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  if (done) {
    return (
      <AuthShell eyebrow="Reset password" title="Password updated.">
        <div className="p-6 rounded-3xl flex items-start gap-3" data-testid="reset-done" style={{ background: "var(--accent-tint)", border: "1px solid var(--accent)" }}>
          <CheckCircle size={22} weight="fill" style={{ color: "var(--accent)" }} />
          <div>
            <div className="font-medium mb-1">You're all set.</div>
            <div className="text-sm" style={{ color: "var(--ink-2)" }}>Redirecting to sign in&hellip;</div>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Reset password"
      title="Set a new password."
      subtitle="Pick something you'll remember. At least six characters."
    >
      <form onSubmit={submit}>
        <label className="block mb-4">
          <div className="eyebrow mb-2">New password</div>
          <div className="relative">
            <input required type={show ? "text" : "password"} data-testid="reset-pw" className="input pr-12" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" autoFocus />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              data-testid="reset-pw-toggle"
              className="absolute inset-y-0 right-2 my-auto h-9 w-9 rounded-full flex items-center justify-center hover:bg-[color:var(--paper)]"
              style={{ color: "var(--ink-2)" }}
              title={show ? "Hide" : "Show"}
              aria-label={show ? "Hide" : "Show"}
            >
              {show ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </label>
        <label className="block mb-6">
          <div className="eyebrow mb-2">Confirm new password</div>
          <input required type={show ? "text" : "password"} data-testid="reset-pw2" className="input" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Type it again" />
        </label>
        <button disabled={busy || !token || !pw || !pw2} type="submit" data-testid="reset-submit" className="btn btn-accent w-full btn-lg mb-4">
          {busy ? <><CircleNotch size={14} className="animate-spin" /> Saving</> : <>Update password <ArrowRight size={16} /></>}
        </button>
        <div className="text-sm" style={{ color: "var(--ink-3)" }}>
          Wrong page? <Link to="/login" className="link-underline" style={{ color: "var(--ink)" }} data-testid="reset-back">Back to sign in →</Link>
        </div>
      </form>
    </AuthShell>
  );
}
